/** Verified Tuolen owner/platform_admin staff supply capabilities, never a token or merchant identity. */
export interface CartiqPlatformHostV1 {
  version: 1;
  /** Non-secret identity + session epoch. A change requires unmount and a fresh mount. */
  contextKey: string;
  actor: { id: string; role: 'owner' | 'platform_admin' };
  request(path: string, init?: RequestInit): Promise<Response>;
  navigate(path: string): void;
  onUnauthorized(): void;
}

export interface CartiqPlatformMountOptions {
  host: CartiqPlatformHostV1;
  path: string;
}

export interface MountedCartiqPlatform {
  /** An allowlisted /superadmin path; never echoes host navigation back to the host. */
  setPath(path: string): void;
  unmount(): void;
}

export const CARTIQ_PLATFORM_MODULE_VERSION = '1.0.0';
export const PLATFORM_ID = '[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

const UI_PATH = new RegExp(
  `^/superadmin(?:/(?:tenants(?:/${PLATFORM_ID})?|users|conversations|orders|products|currencies))?/?$`,
);

export function validatePlatformHost(host: CartiqPlatformHostV1): void {
  if (!host || host.version !== 1 ||
      typeof host.contextKey !== 'string' || !host.contextKey.trim() ||
      typeof host.actor?.id !== 'string' || !host.actor.id.trim() ||
      (host.actor.role !== 'owner' && host.actor.role !== 'platform_admin') ||
      typeof host.request !== 'function' || typeof host.navigate !== 'function' ||
      typeof host.onUnauthorized !== 'function') {
    throw new TypeError('Cartiq platform content requires a version 1 Tuolen platform host with an owner or platform_admin actor.');
  }
}

/** No decoding, URL resolution, query, fragment, alternate casing, or arbitrary suffix. */
export function platformPath(path: string): string {
  if (typeof path !== 'string' || /[%?#\\\u0000-\u0020\u007f]/.test(path) || !UI_PATH.test(path)) {
    throw new TypeError('This path is not part of Cartiq platform administration.');
  }
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

export function isPlatformPath(path: string): boolean {
  try { platformPath(path); return true; } catch { return false; }
}

export const CARTIQ_PLATFORM_NAVIGATION = [
  { label: 'Overview', items: [{ path: '/superadmin', label: 'Overview', icon: 'dashboard' }] },
  { label: 'Accounts', items: [
    { path: '/superadmin/tenants', label: 'Tenants', icon: 'tenants' },
    { path: '/superadmin/users', label: 'Users', icon: 'users' },
  ] },
  { label: 'Activity', items: [
    { path: '/superadmin/conversations', label: 'Conversations', icon: 'conversations' },
    { path: '/superadmin/orders', label: 'Orders', icon: 'orders' },
    { path: '/superadmin/products', label: 'Products', icon: 'products' },
  ] },
  { label: 'Platform', items: [{ path: '/superadmin/currencies', label: 'Currencies', icon: 'currencies' }] },
] as const;
